import {
    BuildingOffice2Icon,
    ClipboardDocumentListIcon,
    EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { ChoiceTileOption, ChoiceTiles } from "components/ui/choice-tiles";
import { useTaskNouns } from "hooks/use-task-nouns";
import React from "react";
import { handleEvent } from "util/user-events";
import { EmailActionEditor } from "../email-action-editor";
import { hasSingleEmailAction, hasSingleTaskAction } from "../manage-workflows-helper";
import { TaskActionEditor } from "../task-action-editor";
import { WorkflowSettingsField } from "../workflow-settings-field";
import { workflowEventProperties } from "./builder-analytics";
import { wizardStepSettings } from "./builder-helper";
import { SequenceStrip } from "./sequence-strip";
import { StepTimingEditor } from "./step-timing-editor";
import { WizardStepProps } from "./wizard-types";
import { stepKey, stepOptions, withStep } from "./workflow-blueprint";

const stepIcon = (key: string, className: string): React.ReactNode => {
    if (key === "task") {
        return <ClipboardDocumentListIcon className={className} />;
    }
    if (key === "email:unselectedCommunities") {
        return <BuildingOffice2Icon className={className} />;
    }
    return <EnvelopeIcon className={className} />;
};

export const WizardSteps: React.FC<WizardStepProps> = props => {
    const {
        workflow,
        workflowSettings,
        editable,
        isDraft,
        canEditStructure,
        onChangeAction,
        editPayload,
        refetch,
        blueprint,
    } = props;
    const nouns = useTaskNouns();

    const isEmail = hasSingleEmailAction(workflow);
    const isTask = hasSingleTaskAction(workflow);
    const stepSettings = wizardStepSettings(workflow.settings || []);

    const eventProperties = React.useMemo(
        () => workflowEventProperties(workflow, blueprint.blueprint),
        [workflow, blueprint.blueprint],
    );

    const commitDelayRecipe = React.useCallback(
        (delayRecipe: string) => {
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "timing",
                delayRecipe,
            });
            editPayload(payload => ({ ...payload, delay: 0, delayRecipe }));
        },
        [editPayload, eventProperties],
    );

    const reportSettingChange = React.useCallback(
        (settingKey: string) => {
            handleEvent("workflow-builder-setting-change", {
                ...eventProperties,
                settingKey,
            });
            refetch();
        },
        [eventProperties, refetch],
    );

    const stepKindOptions = React.useMemo<ChoiceTileOption<string>[]>(() => {
        if (!blueprint.blueprint) {
            return [];
        }
        return stepOptions(blueprint.blueprint, nouns).map(option => ({
            key: option.key,
            title: option.title,
            description: option.description,
            icon: stepIcon(option.key, "w-5 h-5"),
        }));
    }, [blueprint.blueprint, nouns]);

    const handleStepKindSelect = React.useCallback(
        (key: string) => {
            if (!blueprint.blueprint) {
                return;
            }
            const option = stepOptions(blueprint.blueprint, nouns).find(
                candidate => candidate.key === key,
            );
            if (option) {
                blueprint.onApply(withStep(blueprint.blueprint, option.step));
            }
        },
        [blueprint, nouns],
    );

    return (
        <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-4 space-y-4">
                <div className="flex items-center gap-2">
                    <span className="grid place-items-center w-6 h-6 rounded-full bg-brand-600 text-white text-xs font-bold">
                        1
                    </span>
                    {canEditStructure ? (
                        <span className="text-sm font-medium text-gray-700">
                            What should this step do?
                        </span>
                    ) : (
                        <SequenceStrip workflow={workflow} />
                    )}
                </div>
                {canEditStructure ? (
                    <ChoiceTiles
                        options={stepKindOptions}
                        columns={2}
                        value={stepKey(blueprint.blueprint.step)}
                        onSelect={handleStepKindSelect}
                    />
                ) : null}
                {editable && !isDraft && blueprint.blueprint ? (
                    <p className="text-xs text-gray-400">
                        Unpublish this workflow to change what this step does.
                    </p>
                ) : null}
                <StepTimingEditor
                    workflow={workflow}
                    verb={isTask ? "Create" : isEmail ? "Send" : "Run"}
                    onCommit={commitDelayRecipe}
                />
                {stepSettings.map(setting => (
                    <WorkflowSettingsField
                        key={`${workflow.id}.tenant.${setting.key}`}
                        workflow={workflow}
                        setting={setting}
                        workflowSettings={workflowSettings.tenant}
                        onSaved={() => reportSettingChange(setting.key)}
                    />
                ))}
                {isEmail ? (
                    <EmailActionEditor workflow={workflow} onChange={onChangeAction} />
                ) : null}
                {isTask ? (
                    <TaskActionEditor workflow={workflow} onChange={onChangeAction} />
                ) : null}
            </div>
        </div>
    );
};
